import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { LoadingState } from '@/components/app/LoadingState';
import { ErrorState } from '@/components/app/ErrorState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/hooks/useToast';
import { humanizeError } from '@/lib/apiClient';
import {
  funnelObservabilityApi,
  type TriageCoverageGapBody,
} from '../api/funnelObservabilityApi';

const TRIAGE_STATUSES: Array<TriageCoverageGapBody['status']> = [
  'triaged',
  'taxonomy_extension_proposed',
  'resolved',
  'dismissed',
];

interface TriageForm {
  status: TriageCoverageGapBody['status'];
  notes: string;
  proposedNewApproachId: string;
  proposedNewArchetypeId: string;
  proposedNewCategoryKey: string;
  clusterId: string;
}

const EMPTY_FORM: TriageForm = {
  status: 'triaged',
  notes: '',
  proposedNewApproachId: '',
  proposedNewArchetypeId: '',
  proposedNewCategoryKey: '',
  clusterId: '',
};

export function CoverageGapDetailPage() {
  const { gapId = '' } = useParams<{ gapId: string }>();
  const qc = useQueryClient();
  const [form, setForm] = React.useState<TriageForm>(EMPTY_FORM);

  const q = useQuery({
    queryKey: ['ai-coverage-gap', gapId],
    queryFn: () => funnelObservabilityApi.getCoverageGap(gapId),
    enabled: !!gapId,
  });

  const triage = useMutation({
    mutationFn: () =>
      funnelObservabilityApi.triageCoverageGap(gapId, {
        status: form.status,
        notes: form.notes || undefined,
        proposedNewApproachId: form.proposedNewApproachId || undefined,
        proposedNewArchetypeId: form.proposedNewArchetypeId || undefined,
        proposedNewCategoryKey: form.proposedNewCategoryKey || undefined,
        clusterId: form.clusterId || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-coverage-gap', gapId] });
      qc.invalidateQueries({ queryKey: ['ai-coverage-gaps'] });
      toast({ title: 'Coverage gap triaged', variant: 'success' });
    },
    onError: (err) =>
      toast({
        title: 'Triage failed',
        description: humanizeError(err),
        variant: 'destructive',
      }),
  });

  if (q.isLoading) return <LoadingState />;
  if (q.isError || !q.data)
    return (
      <ErrorState title="Coverage gap failed" description={humanizeError(q.error)} />
    );

  const gap = q.data;

  return (
    <div className="space-y-4">
      <Link
        to="/ai/coverage-gaps"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3 w-3" /> Back to coverage gaps
      </Link>

      <PageHeader
        title="Coverage gap"
        description={`Created ${new Date(gap.createdAt).toLocaleString()} · ${gap.gapId}`}
        actions={<Badge>{gap.status}</Badge>}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Goal</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div>
                <Label className="text-xs text-muted-foreground">Category</Label>
                <p>
                  <code className="text-xs">{gap.category}</code>
                </p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Goal text</Label>
                <p className="whitespace-pre-wrap">{gap.goalText}</p>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>userId: <code>{gap.userId}</code></span>
                <span>goalId: <code>{gap.goalId}</code></span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Composer reasoning</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <Label className="text-xs text-muted-foreground">Reason</Label>
                <p>{gap.composerReason}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">
                  Candidates considered ({gap.candidateApproaches.length})
                </Label>
                <div className="mt-1 flex flex-wrap gap-1">
                  {gap.candidateApproaches.length === 0 ? (
                    <span className="text-xs text-muted-foreground">None</span>
                  ) : (
                    gap.candidateApproaches.map((id) => (
                      <Badge key={id} variant="muted" className="font-mono text-[10px]">
                        {id}
                      </Badge>
                    ))
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {gap.freeCompositionUsed && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Free composition output</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  <Label className="text-xs text-muted-foreground">Rendered objective</Label>
                  <p>{gap.renderedObjective ?? '—'}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Coherence score</Label>
                  <p>{gap.coherenceScore?.toFixed(2) ?? '—'} / 5</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Triage</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {gap.triage && (
              <div className="rounded border border-dashed bg-muted/30 p-3 text-xs">
                <p className="font-medium">Last triage</p>
                <p className="text-muted-foreground">
                  {gap.triage.adminUserId}
                  {gap.triagedAt ? ` · ${new Date(gap.triagedAt).toLocaleString()}` : ''}
                </p>
                {gap.triage.notes && (
                  <p className="mt-1 whitespace-pre-wrap">{gap.triage.notes}</p>
                )}
                {(gap.triage.proposedNewApproachId ||
                  gap.triage.proposedNewArchetypeId ||
                  gap.triage.proposedNewCategoryKey) && (
                  <p className="mt-1 text-muted-foreground">
                    proposed: {gap.triage.proposedNewApproachId ?? ''}{' '}
                    {gap.triage.proposedNewArchetypeId ?? ''}{' '}
                    {gap.triage.proposedNewCategoryKey ?? ''}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-1.5">
              <Label>New status</Label>
              <div className="flex flex-wrap gap-2">
                {TRIAGE_STATUSES.map((s) => (
                  <Button
                    key={s}
                    size="sm"
                    variant={form.status === s ? 'default' : 'outline'}
                    onClick={() => setForm((f) => ({ ...f, status: s }))}
                  >
                    {s}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                rows={4}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Why this triage decision?"
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="proposed-approach">Proposed approachId</Label>
                <Input
                  id="proposed-approach"
                  value={form.proposedNewApproachId}
                  onChange={(e) =>
                    setForm({ ...form, proposedNewApproachId: e.target.value })
                  }
                  placeholder="optional"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="proposed-archetype">Proposed archetypeId</Label>
                <Input
                  id="proposed-archetype"
                  value={form.proposedNewArchetypeId}
                  onChange={(e) =>
                    setForm({ ...form, proposedNewArchetypeId: e.target.value })
                  }
                  placeholder="optional"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="proposed-category">Proposed categoryKey</Label>
                <Input
                  id="proposed-category"
                  value={form.proposedNewCategoryKey}
                  onChange={(e) =>
                    setForm({ ...form, proposedNewCategoryKey: e.target.value })
                  }
                  placeholder="optional"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cluster-id">Cluster ID</Label>
                <Input
                  id="cluster-id"
                  value={form.clusterId}
                  onChange={(e) => setForm({ ...form, clusterId: e.target.value })}
                  placeholder="optional"
                />
              </div>
            </div>

            <Separator />
            <Button onClick={() => triage.mutate()} disabled={triage.isPending}>
              {triage.isPending ? 'Saving…' : 'Apply triage'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
